import React from 'react'
import { RenderDialogProps } from '../../../legacy-design-system/action/confirmation-action'
import {
  ConfirmationDialog,
  ConfirmationWarningDialog
} from '../../../legacy-design-system/modal'
import { Mutation, FetchResult } from 'react-apollo'
import gql from 'graphql-tag'
import { FormattedMessage } from 'react-intl'
import { logError, logSuccess } from '../../toaster'
import { PureQueryOptions } from 'apollo-client'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'

interface ConfirmDeleteDialogProps extends RenderDialogProps {
  readonly deleteApplication: () => Promise<FetchResult<any> | void>
}

interface DeleteDialogProps extends RenderDialogProps {
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

interface DeleteDialogSingularProps extends DeleteDialogProps {
  readonly applicationID: string
}

export function ConfirmDeleteAppDialog({
  isOpen,
  closeDialog,
  deleteApplication
}: ConfirmDeleteDialogProps) {
  return (
    <ConfirmationDialog
      size="small"
      isOpen={isOpen}
      onCancel={closeDialog}
      onConfirm={() => {
        closeDialog()
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        deleteApplication()
      }}
      renderConfirmMessage={() => (
        <FormattedMessage id="action_delete" defaultMessage="Delete" />
      )}
      renderCancelMessage={() => (
        <FormattedMessage id="action_cancel" defaultMessage="Cancel" />
      )}
      renderTitle={() => (
        <FormattedMessage
          id="title_deleteApplication"
          defaultMessage="Delete Application"
        />
      )}
      renderBody={() => (
        <FormattedMessage
          id="detail_deleteApplication_confirmation"
          defaultMessage="Are you sure you want to delete the selected application?"
        />
      )}
    />
  )
}

class DeleteMutation extends Mutation<
  any,
  {
    readonly applicationID: string
    readonly organizationID: string
  }
> {}

const deleteApplication = gql`
  mutation DeleteApplication($organizationID: ID!, $applicationID: ID!) {
    deleteApplication(
      input: { organizationID: $organizationID, id: $applicationID }
    ) {
      _placeholder
    }
  }
`

export function DeleteDialog({
  applicationID,
  organizationID,
  refetchQueries,
  refresh,
  ...props
}: DeleteDialogSingularProps) {
  return (
    <DeleteMutation mutation={deleteApplication}>
      {(deleteApplication) => {
        return (
          <ContrastFormattedMessage
            id="detail_applications_appDeletedSuccessfully"
            children={(successMessage) => (
              <ContrastFormattedMessage
                id="detail_applications_appDeletedError"
                children={(errorMessage) => (
                  <ConfirmDeleteAppDialog
                    {...props}
                    deleteApplication={() => {
                      return deleteApplication({
                        refetchQueries,
                        variables: { applicationID, organizationID },
                        optimisticResponse: {
                          deleteApplication: {
                            _placeholder: true,
                            __typename: 'ApplicationBulkResponse'
                          },
                          __typename: 'Mutation'
                        }
                      })
                        .then(() => {
                          // eslint-disable-next-line @typescript-eslint/no-floating-promises
                          refresh()
                          logSuccess(successMessage)
                        })
                        .catch((error) => {
                          logError(errorMessage)
                          return error
                        })
                    }}
                  />
                )}
              />
            )}
          />
        )
      }}
    </DeleteMutation>
  )
}

interface ConfirmDeleteBulkDialogProps extends RenderDialogProps {
  readonly deleteApplications: () => Promise<FetchResult<any> | void>
}

export function ConfirmBulkDeleteAppsDialog({
  isOpen,
  closeDialog,
  deleteApplications
}: ConfirmDeleteBulkDialogProps) {
  return (
    <ConfirmationWarningDialog
      size="small"
      isOpen={isOpen}
      onCancel={closeDialog}
      onConfirm={() => {
        closeDialog()
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        deleteApplications()
      }}
      renderConfirmMessage={() => <FormattedMessage id="action_delete" />}
      renderCancelMessage={() => <FormattedMessage id="action_cancel" />}
      renderTitle={() => <FormattedMessage id="title_deleteApplications" />}
      renderBody={() => (
        <div>
          <div>
            <FormattedMessage id="detail_deleteApplication_warningConfirmation1" />
          </div>
          <div>
            <FormattedMessage id="detail_deleteApplication_warningConfirmation2" />
          </div>
        </div>
      )}
    />
  )
}

interface BulkProps {
  readonly applicationIDs: string[]
}

const deleteApplications = gql`
  mutation DeleteApplications($organizationID: ID!, $applicationIDs: [ID!]!) {
    deleteApplications(
      input: { organizationID: $organizationID, ids: $applicationIDs }
    ) {
      _placeholder
    }
  }
`

export function BulkDeleteDialog({
  applicationIDs,
  organizationID,
  refetchQueries,
  refresh,
  ...props
}: BulkProps & DeleteDialogProps) {
  return (
    <Mutation mutation={deleteApplications}>
      {(deleteApplications) => (
        <ContrastFormattedMessage
          id="detail_applications_appDeletedSuccessfully"
          children={(successMessage) => (
            <ContrastFormattedMessage
              id="detail_applications_appDeletedError"
              children={(errorMessage) => (
                <ConfirmBulkDeleteAppsDialog
                  {...props}
                  deleteApplications={() => {
                    return deleteApplications({
                      refetchQueries,
                      variables: { applicationIDs, organizationID },
                      optimisticResponse: {
                        deleteApplications: {
                          _placeholder: true,
                          __typename: 'ApplicationBulkResponse'
                        },
                        __typename: 'Mutation'
                      }
                    })
                      .then(() => {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        refresh()
                        logSuccess(successMessage)
                      })
                      .catch((error) => {
                        logError(errorMessage)
                        return error
                      })
                  }}
                />
              )}
            />
          )}
        />
      )}
    </Mutation>
  )
}

const deleteApplicationsByFilters = gql`
  mutation DeleteApplicationsByFilters(
    $organizationID: ID!
    $excludedApplications: [ID!]!
    $filters: ApplicationFilterArg!
  ) {
    deleteApplicationsByFilters(
      input: {
        organizationID: $organizationID
        excludedApplications: $excludedApplications
        filters: $filters
      }
    ) {
      _placeholder
    }
  }
`

interface BulkWithFilterProps {
  readonly excludedApplications: string[]
  readonly filters: any
}

export function BulkDeleteWithFiltersDialog({
  excludedApplications,
  organizationID,
  filters,
  refetchQueries,
  refresh,
  ...props
}: BulkWithFilterProps & DeleteDialogProps) {
  return (
    <Mutation mutation={deleteApplicationsByFilters}>
      {(deleteApplications) => (
        <ContrastFormattedMessage
          id="detail_applications_appDeletedSuccessfully"
          children={(successMessage) => (
            <ContrastFormattedMessage
              id="detail_applications_appDeletedError"
              children={(errorMessage) => (
                <ConfirmBulkDeleteAppsDialog
                  {...props}
                  deleteApplications={() => {
                    return deleteApplications({
                      refetchQueries,
                      variables: {
                        excludedApplications,
                        organizationID,
                        filters
                      },
                      optimisticResponse: {
                        deleteApplicationsByFilters: {
                          _placeholder: true,
                          __typename: 'ApplicationBulkResponse'
                        },
                        __typename: 'Mutation'
                      }
                    })
                      .then(() => {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        refresh()
                        logSuccess(successMessage)
                      })
                      .catch((error) => {
                        logError(errorMessage)
                        return error
                      })
                  }}
                />
              )}
            />
          )}
        />
      )}
    </Mutation>
  )
}
